// ============================================================
// frontend/src/pages/Factures.js
// Gestion des factures de vente + intégration Peppol Peppyrus
// Peppol : visible UNIQUEMENT pour les clients avec numéro de TVA (PRO)
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatNomClient } from '../utils/clientHelpers';
import { useSearchParams } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import theme from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Vrai email vs adresse auto-générée c{n}@noreply.mkw-solutions.be
const isRealEmail = (email) =>
  !!(email && !/^c\d+@noreply\.mkw-solutions\.be$/.test(email));

// ── Styles constants ──────────────────────────────────────────────────────────
const S = {
  btn: {
    border: 'none', borderRadius: '6px', cursor: 'pointer',
    fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '5px',
    fontSize: '12px', padding: '6px 11px', transition: 'opacity .15s',
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'white', borderRadius: '14px', padding: '28px',
    maxWidth: '760px', width: '96%', maxHeight: '85vh',
    overflowY: 'auto', boxShadow: theme.shadowLg,
  },
};

// ── Badge statut paiement ─────────────────────────────────────────────────────
const StatutBadge = ({ statut }) => {
  const cfg = theme.paiement[statut] || theme.paiement.non_payee;
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      padding: '3px 11px', borderRadius: '20px', fontSize: '12px',
      fontWeight: '600', whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
};

// ── Badge Peppol ──────────────────────────────────────────────────────────────
const PeppolBadge = ({ sent, loading }) => {
  if (loading) return (
    <span style={{ ...S.btn, background: '#e0e0e0', color: '#666', cursor: 'default' }}>
      ⏳ …
    </span>
  );
  if (sent) return (
    <span style={{ ...S.btn, background: theme.successBg, color: theme.success, cursor: 'default' }}>
      ✅ Peppol envoyé
    </span>
  );
  // PENSE-BÊTE : facture pro non encore envoyée via Peppol
  return (
    <span title="Cette facture n'a pas encore été envoyée via le réseau Peppol. Utilisez FacturesPro pour l'envoyer." style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '3px 8px', borderRadius: '6px',
      background: '#fff3cd', color: '#856404',
      border: '1px solid #ffc107',
      fontSize: '11px', fontWeight: '700',
      cursor: 'help',
      whiteSpace: 'nowrap',
    }}>
      ⚠️ Peppol non envoyé
    </span>
  );
};

// ── Badge PRO client ──────────────────────────────────────────────────────────
const ProBadge = () => (
  <span style={{
    background: '#e3f2fd', color: '#1565c0',
    fontSize: '10px', fontWeight: '700', padding: '2px 6px',
    borderRadius: '4px', letterSpacing: '0.5px',
  }}>PRO</span>
);

// ── Card statistique ──────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, color, sub, wrapStyle }) => (
  <div style={{ background: 'white', borderRadius: '12px', padding: '16px 18px', boxShadow: theme.shadow, borderLeft: `4px solid ${color}`, ...wrapStyle }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <span style={{ fontSize: '26px' }}>{icon}</span>
      <div>
        <div style={{ fontSize: '20px', fontWeight: '700', color }}>{value}</div>
        <div style={{ fontSize: '12px', color: theme.textMuted }}>{label}</div>
        {sub && <div style={{ fontSize: '11px', color: theme.textMuted, marginTop: '2px' }}>{sub}</div>}
      </div>
    </div>
  </div>
);

// ═════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═════════════════════════════════════════════════════════════════════════════
const Factures = () => {
  const [factures,        setFactures]        = useState([]);
  const [stats,           setStats]           = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [expandedId,      setExpandedId]      = useState(null);

  // Peppol states
  const [peppolStatus,    setPeppolStatus]    = useState({});  // { id_facture: { sent, peppol_status, peppol_message_id, date_envoi, peppol_detail } }
  const [peppolLoading,   setPeppolLoading]   = useState({});  // { id_facture: true/false }
  const [peppolStatusLoading, setPeppolStatusLoading] = useState({});

  // Modal prévisualisation XML
  const [xmlPreview,      setXmlPreview]      = useState(null); // { xml, client_nom, ... }
  const [showXmlModal,    setShowXmlModal]    = useState(false);
  const [showPaiModal,   setShowPaiModal]   = useState(false);
  const [paiTarget,      setPaiTarget]      = useState(null);
  const [paiForm,        setPaiForm]        = useState({ statut_paiement: 'payee', montant_paye: '' });
  const [paiLoading,     setPaiLoading]     = useState(false);

  // ── Recherche et filtres ──────────────────────────────────────────────────
  const [search,        setSearch]        = useState('');
  const [filtreStatut,  setFiltreStatut]  = useState('tous');
  const [periodeType,   setPeriodeType]   = useState('tout');   // 'tout' | 'mois' | 'trimestre'
  const [periodeAnnee,  setPeriodeAnnee]  = useState(new Date().getFullYear());
  const [periodeMois,   setPeriodeMois]   = useState(new Date().getMonth());      // 0-11
  const [periodeQ,      setPeriodeQ]      = useState(Math.floor(new Date().getMonth() / 3)); // 0-3

  // ── Modal virement (F4 client) ───────────────────────────────────────────
  const [modalVirement,  setModalVirement]  = useState(null);  // facture courante
  const [virInfos,       setVirInfos]       = useState(null);
  const [virLoading,     setVirLoading]     = useState(false);
  const [virSent,        setVirSent]        = useState(false);
  const [showRectifModal,  setShowRectifModal]  = useState(false);
  const [rectifForm,       setRectifForm]       = useState({
    id_client: '', taux_tva: '21', description: '',
    motif_correction: '', facture_rectifiee: '', note_credit_ref: '', id_devis: ''
    // montant_ht retiré : dérivé des lignes en étape 2
  });
  const [rectifLoading,    setRectifLoading]    = useState(false);
  const [rectifStep,       setRectifStep]       = useState(1);       // 1=sélection, 2=lignes
  const [rectifLignes,     setRectifLignes]     = useState([]);      // lignes éditables étape 2
  const [clients,          setClients]          = useState([]);
  const [clientFactures,   setClientFactures]   = useState([]); // factures du client sélectionné
  const [virError,       setVirError]       = useState('');
  const [notesCredit,    setNotesCredit]    = useState([]);

  const user     = JSON.parse(localStorage.getItem('user') || '{}');
  const token    = localStorage.getItem('token');
  const config   = { headers: { Authorization: `Bearer ${token}` } };
  const isAdmin  = user.role === 'admin';
  const isMobile = useIsMobile();

  // ── Chargement factures ────────────────────────────────────────────────────
  const fetchFactures = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [axios.get(`${API_URL}/factures`, config)];
      if (isAdmin) requests.push(axios.get(`${API_URL}/factures/stats`, config));
      const [res, statsRes] = await Promise.all(requests);
      let data   = res.data.data || [];
      if (!isAdmin) {
        data = data.filter(f => f.id_client === user.id_client);
      }
      setFactures(data);
      if (statsRes) setStats(statsRes.data.data || null);
      // Charger les statuts Peppol pour les factures PRO uniquement
      data.forEach(f => {
        if (f.Client?.numero_tva) checkPeppolStatus(f.id_facture);
      });
    } catch (err) {
      console.error('[Factures] fetchFactures:', err.message);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchFactures(); }, [fetchFactures]);

  // ── Chargement clients (pour le modal facture rectificative) ────────────────
  useEffect(() => {
    if (!isAdmin) return;
    const token  = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API_URL}/clients`, config)
      .then(r => setClients(r.data?.data || r.data || []))
      .catch(() => {});
  }, [isAdmin]);

  // ── Chargement notes de crédit (pour déduction HT/TTC dans la bande récap) ─
  useEffect(() => {
    const token  = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API_URL}/notes-credit`, config)
      .then(r => setNotesCredit(r.data?.data || r.data || []))
      .catch(() => {});
  }, []);
  const [searchParams] = useSearchParams();

  // Deep link email: /factures?virement=FACT-XXX → ouvre modal virement auto
  useEffect(() => {
    const virParam = searchParams.get('virement');
    if (!virParam || factures.length === 0) return;
    const target = factures.find(f => f.numero_facture === virParam);
    if (target) {
      openVirement(target);
      // Nettoyer le param URL sans recharger la page
      window.history.replaceState({}, '', window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factures, searchParams]);

  // ── Statut Peppol (par facture) ────────────────────────────────────────────
  const checkPeppolStatus = async (id) => {
    setPeppolStatusLoading(prev => ({ ...prev, [id]: true }));
    try {
      const res = await axios.get(`${API_URL}/peppol/facture/${id}/status`, config);
      const d = res.data.data || {};
      setPeppolStatus(prev => ({ ...prev, [id]: {
        sent:              d.sent              || false,
        peppol_status:     d.peppol_status     || null,
        peppol_message_id: d.peppol_message_id || null,
        date_envoi:        d.date_envoi        || null,
        peppol_detail:     d.peppol_detail     || null,
      }}));
    } catch (err) {
      console.error(`[Peppol] statut facture ${id}:`, err.message);
    } finally {
      setPeppolStatusLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  // ── Téléchargement PDF ─────────────────────────────────────────────────────

  // ── Marquer paiement ───────────────────────────────────────────────────────
  const openPaiModal = (f) => {
    setPaiTarget(f);
    setPaiForm({
      statut_paiement: f.statut_paiement === 'payee' ? 'non_payee' : 'payee',
      montant_paye: f.montant_ttc || '',
    });
    setShowPaiModal(true);
  };

  // ── Réinitialiser statut Peppol (admin — pour renvoi après rejet) ────────────
  const resetPeppol = async (f) => {
    if (!window.confirm(
      'Réinitialiser le statut Peppol de ' + f.numero_facture + ' ?\n\n' +
      '⚠️ À utiliser UNIQUEMENT si la facture a été rejetée ou si l\'envoi a échoué.\n' +
      'Cela permettra un renvoi. Si le client a déjà reçu la facture, émettez une NC.'
    )) return;
    try {
      await axios.patch(`${API_URL}/factures/${f.id_facture}/reset-peppol`, {}, config);
      alert('✅ Statut Peppol réinitialisé. Vous pouvez renvoyer la facture.');
      fetchFactures();
      checkPeppolStatus(f.id_facture);
    } catch (e) { alert('Erreur : ' + (e.response?.data?.message || e.message)); }
  };

  // ── Supprimer une facture (admin only) ────────────────────────────
  const deleteFacture = async (f) => {
    if (f.envoyee_peppol) {
      alert('Cette facture a ete envoyee via Peppol. Emettez une note de credit pour l annuler.');
      return;
    }
    if (!window.confirm('Supprimer definitivement la facture ' + f.numero_facture + ' ?\nCette action est irreversible.')) return;
    try {
      await axios.delete(`${API_URL}/factures/${f.id_facture}`, config);
      alert('Facture ' + f.numero_facture + ' supprimee.');
      fetchFactures();
    } catch (e) { alert('Erreur : ' + (e.response?.data?.message || e.message)); }
  };

  // ── Créer une facture rectificative / indépendante ──────────────────────────
  const creerFactureRectificative = async () => {
    const { id_client, description, motif_correction, facture_rectifiee, note_credit_ref, id_devis, taux_tva } = rectifForm;
    if (!id_client)             return alert('Sélectionnez un client.');
    if (!description?.trim())   return alert('Saisissez une description.');
    if (rectifLignes.length === 0) return alert('Ajoutez au moins un poste.');
    // Calculer montant HT depuis les lignes éditées
    const htTotal  = rectifLignes.reduce((s,l) => s + parseFloat(l.quantite||0)*parseFloat(l.prix_unitaire||0), 0);
    // Calculer TVA réelle ligne par ligne (supporte les taux mixtes 6%+21%)
    const tvaTotal = rectifLignes.reduce((s,l) => s + parseFloat(l.quantite||0)*parseFloat(l.prix_unitaire||0)*parseFloat(l.tva||0)/100, 0);
    const ttcTotal = htTotal + tvaTotal;
    if (htTotal <= 0) return alert('Le montant total HT doit être supérieur à 0.');
    setRectifLoading(true);
    try {
      await axios.post(`${API_URL}/factures/independante`, {
        id_client:         parseInt(id_client),
        montant_ht:        parseFloat(htTotal.toFixed(2)),
        montant_tva:       parseFloat(tvaTotal.toFixed(2)),
        montant_ttc:       parseFloat(ttcTotal.toFixed(2)),
        taux_tva:          parseFloat(taux_tva) >= 0 ? parseFloat(taux_tva) : 21,
        description:       description.trim(),
        motif_correction:  motif_correction?.trim()  || null,
        facture_rectifiee: facture_rectifiee?.trim()  || null,
        note_credit_ref:   note_credit_ref?.trim()    || null,
        id_devis:          id_devis ? parseInt(id_devis) : null,
        lignes_editees:    rectifLignes,  // envoi des lignes modifiées pour usage futur
      }, config);
      setShowRectifModal(false);
      setRectifStep(1);
      setRectifForm({ id_client:'', taux_tva:'21', description:'',
        motif_correction:'', facture_rectifiee:'', note_credit_ref:'', id_devis:'' });
      setRectifLignes([]);
      setClientFactures([]);
      fetchFactures();
      alert('✅ Facture rectificative créée avec succès.');
    } catch (e) { alert('Erreur : ' + (e.response?.data?.message || e.message)); }
    finally { setRectifLoading(false); }
  };

  const saveStatutPaiement = async () => {
    if (!paiTarget) return;
    setPaiLoading(true);
    try {
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API_URL}/factures/${paiTarget.id_facture}/statut-paiement`, {
        statut_paiement: paiForm.statut_paiement,
        montant_paye:    paiForm.statut_paiement === 'payee' ? parseFloat(paiForm.montant_paye) : undefined,
      }, cfg);
      setShowPaiModal(false);
      await fetchFactures();
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.message || e.message));
    } finally {
      setPaiLoading(false);
    }
  };

  // ── Virement (F4) ─────────────────────────────────────────────────────────
  const openVirement = async (f) => {
    setModalVirement(f);
    setVirInfos(null);
    setVirSent(false);
    setVirError('');
    setVirLoading(true);
    try {
      const r = await axios.post(`${API_URL}/factures/${f.id_facture}/demande-virement`, {}, config);
      if (r.data.success) { setVirInfos(r.data.data); setVirSent(true); }
    } catch (e) {
      setVirError(e.response?.data?.message || 'Erreur lors de la récupération des informations.');
    } finally {
      setVirLoading(false);
    }
  };

  const downloadPDF = async (id, numero) => {
    try {
      const res = await axios.get(`${API_URL}/factures/${id}/pdf`, {
        ...config, responseType: 'blob',
      });
      const contentType = res.headers['content-type'] || '';
      if (!contentType.includes('application/pdf')) {
        const text = await new Promise(r => { const rd = new FileReader(); rd.onload = () => r(rd.result); rd.readAsText(res.data); });
        let msg = 'Erreur PDF';
        try { msg = JSON.parse(text).message || msg; } catch {}
        return alert('Erreur : ' + msg);
      }
      const url  = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `facture-${numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erreur PDF : ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Téléchargement XML UBL ─────────────────────────────────────────────────
  const downloadXML = async (id, numero) => {
    try {
      const res = await axios.get(`${API_URL}/peppol/facture/${id}/xml`, {
        ...config, responseType: 'blob',
      });
      const url  = window.URL.createObjectURL(new Blob([res.data], { type: 'application/xml' }));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `PEPPOL_${numero}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      alert('Erreur XML : ' + msg);
    }
  };

  // ── Prévisualisation XML ───────────────────────────────────────────────────
  const previewXML = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/peppol/facture/${id}/preview`, config);
      setXmlPreview(res.data.data);
      setShowXmlModal(true);
    } catch (err) {
      alert('Erreur prévisualisation : ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Envoi Peppol ──────────────────────────────────────────────────────────
  const sendPeppol = async (facture) => {
    // Double vérification client PRO
    if (!facture.Client?.numero_tva) {
      return alert('⚠️ Ce client n\'a pas de numéro de TVA. Peppol est réservé aux clients professionnels assujettis à la TVA.');
    }
    if (peppolStatus[facture.id_facture]?.sent) {
      return alert('Cette facture a déjà été envoyée via Peppol.');
    }

    const confirm_msg =
      `Envoyer la facture ${facture.numero_facture} via le réseau Peppol ?\n\n` +
      `Client : ${formatNomClient(facture.Client)}\n` +
      `N° TVA : ${facture.Client.numero_tva}\n` +
      `Montant : ${parseFloat(facture.montant_ttc).toFixed(2)} EUR\n\n` +
      `⚠️ Cette action est irréversible.`;

    if (!window.confirm(confirm_msg)) return;

    setPeppolLoading(prev => ({ ...prev, [facture.id_facture]: true }));
    try {
      const res = await axios.post(
        `${API_URL}/peppol/facture/${facture.id_facture}/send`, {}, config
      );
      if (res.data.success) {
        setPeppolStatus(prev => ({ ...prev, [facture.id_facture]: true }));
        const msgId = res.data.data?.id || res.data.data?.messageId || res.data.data?.peppyrus_id || '—';
        alert(`✅ Facture envoyée via Peppol !\nID Peppyrus : ${msgId}`);
        checkPeppolStatus(facture.id_facture);
        fetchFactures();
      }
    } catch (err) {
      const code = err.response?.data?.code;
      const msg  = err.response?.data?.message || err.message;
      if (code === 'ALREADY_SENT') {
        // Déjà envoyé → mettre à jour le statut local sans alerter en erreur
        setPeppolStatus(prev => ({ ...prev, [facture.id_facture]: true }));
        alert(`ℹ️ ${msg}`);
      } else {
        alert(`❌ Erreur Peppol : ${msg}`);
      }
    } finally {
      setPeppolLoading(prev => ({ ...prev, [facture.id_facture]: false }));
    }
  };


  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: theme.pageBg }}>

      {/* ── Header ── */}
      <div style={{ background: theme.gradientHeader, padding: isMobile ? '14px 14px' : '20px 24px', color: 'white' }}>
        <div style={{ maxWidth: '1300px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '700' }}>
              🧾 {isAdmin ? 'Gestion des Factures' : 'Mes Factures'}
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.85 }}>
              {factures.length} facture{factures.length !== 1 ? 's' : ''}
              {isAdmin && ` · ${factures.filter(f => f.Client?.numero_tva).length} clients PRO`}
            </p>
          </div>

          {/* ── Bouton Facture Rectificative / Indépendante (admin) ── */}
          {isAdmin && (
            <button
              onClick={() => setShowRectifModal(true)}
              style={{ padding: '8px 16px', borderRadius: '8px', border: 'none',
                background: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: '600',
                fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              title="Créer une facture rectificative ou complémentaire sans devis">
              ➕ Facture rectificative
            </button>
          )}

        </div>
      </div>

      <div style={{ maxWidth: '1300px', margin: '0 auto', padding: isMobile ? '12px 10px' : '24px' }}>

        {/* ── Bandeau info Peppol admin ── */}
        {isAdmin && (
          <div style={{
            background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
            padding: '16px 22px', borderRadius: '12px', marginBottom: '20px',
            display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: '32px' }}>🌐</span>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'white', fontWeight: '700', fontSize: '15px' }}>
                PEPPOL activé — Access Point : Peppyrus
              </div>
              <div style={{ color: '#90caf9', fontSize: '13px', marginTop: '3px' }}>
                Facturation électronique B2B obligatoire en Belgique (loi 06/07/2023).
                Les boutons Peppol s'activent automatiquement pour les clients avec numéro de TVA.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ ...S.btn, background: 'rgba(255,255,255,.15)', color: 'white', border: '1px solid rgba(255,255,255,.3)', cursor: 'default' }}>
                📄 XML = fichier UBL 2.1
              </span>
              <span style={{ ...S.btn, background: 'rgba(255,255,255,.15)', color: 'white', border: '1px solid rgba(255,255,255,.3)', cursor: 'default' }}>
                📤 Envoi = réseau Peppol
              </span>
            </div>
          </div>
        )}

        {/* ── KPI Stats (admin seulement) ── */}
        {isAdmin && stats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap: isMobile ? '10px' : '16px',
            marginBottom: '20px',
          }}>
            <StatCard icon="🧾" label="Total émises"
              value={stats.total}
              color={theme.primary} />
            <StatCard icon="⏳" label="En retard"
              value={stats.en_retard || 0}
              color={theme.danger}
              sub={stats.en_retard > 0 ? 'Échéance dépassée' : 'Tout à jour'} />
            <StatCard icon="💶" label="Encours à percevoir"
              value={stats.total_encours?.toLocaleString('fr-BE', { style: 'currency', currency: 'EUR' }) || '—'}
              color={theme.warning || '#f39c12'} />
            <StatCard icon="✅" label="Total encaissé"
              value={stats.total_paye?.toLocaleString('fr-BE', { style: 'currency', currency: 'EUR' }) || '—'}
              color={theme.success} />
          </div>
        )}

        {/* ── Barre de recherche + filtres ── */}
        {(() => {
          const STATUTS_FILTER = [
            { key: 'tous',      label: 'Toutes' },
            { key: 'emise',     label: '📤 Émises' },
            { key: 'non_payee', label: '⏳ Non payées' },
            { key: 'partielle', label: '🔶 Partielles' },
            { key: 'payee',     label: '✅ Payées' },
            { key: 'en_retard', label: '🔴 En retard' },
          ];
          const MOIS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
          const anneesDisponibles = (() => {
            const ys = [...new Set(
              factures.map(f => new Date(f.date_emission).getFullYear()).filter(y => !isNaN(y) && y > 2000)
            )].sort((a, b) => b - a);
            if (!ys.includes(new Date().getFullYear())) ys.unshift(new Date().getFullYear());
            return ys;
          })();
          const matchDate = (dateStr) => {
            if (periodeType === 'tout') return true;
            const d = new Date(dateStr);
            if (isNaN(d)) return false;
            if (d.getFullYear() !== periodeAnnee) return false;
            if (periodeType === 'mois')      return d.getMonth() === periodeMois;
            if (periodeType === 'trimestre') return Math.floor(d.getMonth() / 3) === periodeQ;
            return true;
          };
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const facturesFiltrees = factures.filter(f => {
            const isEnRetard = f.date_echeance
              && new Date(f.date_echeance) < today
              && !['payee', 'annulee', 'brouillon'].includes(f.statut_paiement);
            const matchStatut = filtreStatut === 'tous'
              || (filtreStatut === 'en_retard' ? isEnRetard : f.statut_paiement === filtreStatut);
            if (!matchStatut) return false;
            if (!matchDate(f.date_emission)) return false;
            if (!search.trim()) return true;
            const q = search.toLowerCase();
            return (
              (f.numero_facture || '').toLowerCase().includes(q) ||
              (f.Client?.nom    || '').toLowerCase().includes(q) ||
              (f.Client?.prenom || '').toLowerCase().includes(q) ||
              (f.Client?.email  || '').toLowerCase().includes(q) ||
              String(f.montant_ttc || '').includes(q)
            );
          });
          // Construire la carte NC par facture (HT et TTC) pour déduire des totaux
          const ncMap = {};
          notesCredit.forEach(nc => {
            const id = nc.id_facture;
            const htNC  = parseFloat(nc.montant  || 0);
            const ttcNC = htNC * (1 + parseFloat(nc.taux_tva || 0) / 100);
            ncMap[id] = { ht: (ncMap[id]?.ht || 0) + htNC, ttc: (ncMap[id]?.ttc || 0) + ttcNC };
          });
          const totalHT  = facturesFiltrees.reduce((s,f) => s + Math.max(0, parseFloat(f.montant_ht  || 0) - (ncMap[f.id_facture]?.ht  || 0)), 0);
          const totalTTC = facturesFiltrees.reduce((s,f) => s + Math.max(0, parseFloat(f.montant_ttc || 0) - (ncMap[f.id_facture]?.ttc || 0)), 0);
          const fmtEur = v => v.toLocaleString('fr-BE', { style:'currency', currency:'EUR' });
          return (
            <>
              <div style={{
                background: 'white', borderRadius: '12px', padding: '14px 16px',
                marginBottom: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              }}>
                {/* Ligne 1 : recherche */}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={isAdmin ? '🔍 N° facture, client, montant…' : '🔍 N° facture, montant…'}
                    style={{
                      flex: '1 1 200px', padding: '8px 12px', border: '1.5px solid #dce8f0',
                      borderRadius: '8px', fontSize: '13px', outline: 'none',
                      background: '#f8fafc', minWidth: '0', width: isMobile ? '100%' : undefined,
                    }}
                  />
                </div>
                {/* Ligne 2 : filtres statut */}
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  {STATUTS_FILTER.map(sf => (
                    <button key={sf.key} onClick={() => setFiltreStatut(sf.key)} style={{
                      padding: isMobile ? '5px 9px' : '6px 12px', borderRadius: '20px', border: 'none',
                      cursor: 'pointer', fontSize: isMobile ? '11px' : '12px', fontWeight: '600',
                      background: filtreStatut === sf.key ? theme.primary : '#f0f2f5',
                      color:      filtreStatut === sf.key ? 'white'       : '#666',
                      transition: 'all 0.15s', whiteSpace: 'nowrap',
                    }}>
                      {sf.label}
                    </button>
                  ))}
                </div>
                {/* Ligne 3 : sélecteur de période */}
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', color: '#999', fontWeight: '600', whiteSpace: 'nowrap' }}>Période :</span>
                  {/* Mode : Tout / Mois / Trimestre */}
                  {[{k:'tout',l:'Tout'},{k:'mois',l:'Mois'},{k:'trimestre',l:'Trimestre'}].map(({k,l}) => (
                    <button key={k} onClick={() => setPeriodeType(k)} style={{
                      padding: isMobile ? '6px 11px' : '6px 13px', borderRadius: '20px', border: 'none',
                      cursor: 'pointer', fontSize: isMobile ? '11px' : '12px', fontWeight: '600',
                      background: periodeType === k ? '#7c3aed' : '#f0f2f5',
                      color:      periodeType === k ? 'white'   : '#666',
                      transition: 'all 0.15s', whiteSpace: 'nowrap',
                    }}>{l}</button>
                  ))}
                  {/* Sélecteur année — visible si mode !== tout */}
                  {periodeType !== 'tout' && (
                    <select
                      value={periodeAnnee}
                      onChange={e => setPeriodeAnnee(parseInt(e.target.value))}
                      style={{
                        padding: isMobile ? '6px 8px' : '6px 10px', borderRadius: '8px',
                        border: '1.5px solid #7c3aed', fontSize: isMobile ? '12px' : '13px',
                        background: '#faf5ff', color: '#7c3aed', fontWeight: '700',
                        cursor: 'pointer', outline: 'none',
                      }}
                    >
                      {anneesDisponibles.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  )}
                  {/* Sélecteur mois (natif iOS) */}
                  {periodeType === 'mois' && (
                    <select
                      value={periodeMois}
                      onChange={e => setPeriodeMois(parseInt(e.target.value))}
                      style={{
                        padding: isMobile ? '6px 8px' : '6px 10px', borderRadius: '8px',
                        border: '1.5px solid #7c3aed', fontSize: isMobile ? '12px' : '13px',
                        background: '#faf5ff', color: '#5b21b6', fontWeight: '600',
                        cursor: 'pointer', outline: 'none',
                      }}
                    >
                      {MOIS_FR.map((m, i) => <option key={i} value={i}>{m}</option>)}
                    </select>
                  )}
                  {/* Boutons Q1-Q4 */}
                  {periodeType === 'trimestre' && (
                    <>
                      {[{q:0,l:'Q1'},{q:1,l:'Q2'},{q:2,l:'Q3'},{q:3,l:'Q4'}].map(({q,l}) => (
                        <button key={q} onClick={() => setPeriodeQ(q)} style={{
                          padding: isMobile ? '6px 11px' : '6px 13px', borderRadius: '8px', border: 'none',
                          cursor: 'pointer', fontSize: isMobile ? '12px' : '13px', fontWeight: '700',
                          background: periodeQ === q ? '#7c3aed' : '#ede9fe',
                          color:      periodeQ === q ? 'white'   : '#5b21b6',
                          transition: 'all 0.15s', minWidth: isMobile ? '40px' : '44px',
                        }}>{l}</button>
                      ))}
                    </>
                  )}
                </div>
              </div>

              {/* ── Bande récapitulative HT / TTC ── */}
              {facturesFiltrees.length > 0 && (
                <div style={{
                  display: 'flex', gap: isMobile ? '8px' : '16px',
                  flexWrap: 'wrap', alignItems: 'center', justifyContent: isMobile ? 'space-between' : 'flex-end',
                  background: 'white', borderRadius: '10px', padding: isMobile ? '9px 12px' : '8px 16px',
                  marginBottom: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                  fontSize: isMobile ? '12px' : '13px',
                }}>
                  <span style={{ color: '#888', fontWeight: 500 }}>
                    {facturesFiltrees.length} facture{facturesFiltrees.length > 1 ? 's' : ''}
                  </span>
                  <span style={{ fontWeight: 600, color: '#555' }}>
                    HT : <span style={{ color: theme.primary }}>{fmtEur(totalHT)}</span>
                  </span>
                  <span style={{ fontWeight: 700, color: '#333' }}>
                    TTC : <span style={{ color: theme.primary }}>{fmtEur(totalTTC)}</span>
                  </span>
                </div>
              )}

        {/* ── Tableau factures ── */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: theme.textMuted, fontSize: '16px' }}>
            ⏳ Chargement des factures…
          </div>
        ) : facturesFiltrees.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', background: 'white', borderRadius: '12px', color: theme.textMuted }}>
            <p style={{ fontSize: '48px', margin: '0 0 16px' }}>🧾</p>
            <p style={{ fontSize: '16px' }}>{factures.length === 0 ? "Aucune facture pour l'instant." : "Aucune facture ne correspond à votre recherche."}</p>
          </div>
        ) : (
          <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: theme.shadow }}>
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '440px' : '600px' }}>
              <thead>
                <tr style={{ background: theme.primary, color: 'white' }}>
                  <th style={{ padding: '13px 14px', textAlign: 'left', fontSize: '13px' }}>N° Facture</th>
                  {isAdmin && <th style={{ padding: '13px 14px', textAlign: 'left', fontSize: '13px' }}>Client</th>}
                  <th style={{ padding: '13px 14px', textAlign: 'left', fontSize: '13px' }}>Date</th>
                  {!isMobile && <th style={{ padding: '13px 14px', textAlign: 'left', fontSize: '13px' }}>Échéance</th>}
                  <th style={{ padding: '13px 14px', textAlign: 'right', fontSize: '13px' }}>Montant TTC</th>
                  <th style={{ padding: '13px 14px', textAlign: 'center', fontSize: '13px' }}>Statut</th>
                  <th style={{ padding: '13px 14px', textAlign: 'center', fontSize: '13px' }}>PDF</th>
                  {isAdmin && <th style={{ padding: '13px 14px', textAlign: 'center', fontSize: '13px' }}>Peppol B2B</th>}
                </tr>
              </thead>
              <tbody>
                {facturesFiltrees.map(f => {
                  const isPro      = !!(f.Client?.numero_tva?.trim());
                  const isSent     = peppolStatus[f.id_facture]?.sent || f.envoyee_peppol || false;
                  const isLoading  = peppolLoading[f.id_facture] || false;
                  const isStsLoading = peppolStatusLoading[f.id_facture] || false;
                  const isExpanded = expandedId === f.id_facture;
                  const echeanceDepassee = f.date_echeance
                    && new Date(f.date_echeance) < new Date()
                    && !['payee', 'annulee'].includes(f.statut_paiement);
                  const rowBg      = isExpanded ? '#f8fafc' : (f.statut_paiement === 'en_retard' || echeanceDepassee ? '#fff8f8' : 'white');

                  return (
                    <React.Fragment key={f.id_facture}>
                      {/* ── Ligne principale ── */}
                      {isMobile ? (
                        <tr style={{ borderBottom: '1px solid #e5eaf0', background: rowBg }}
                          onClick={() => setExpandedId(isExpanded ? null : f.id_facture)}>
                          <td style={{ padding:'10px 12px' }}>
                            {/* Ligne 1 : N° + Montant */}
                            <div style={{ display:'flex', justifyContent:'space-between',
                              alignItems:'flex-start', marginBottom:'5px' }}>
                              <div>
                                <div style={{ fontWeight:'800', fontSize:'14px', color:theme.primary }}>
                                  {f.numero_facture}
                                  {f.numero_echeance && (
                                    <span style={{ marginLeft:'6px', fontSize:'10px', background:theme.primaryBg,
                                      color:theme.primary, padding:'1px 5px', borderRadius:'4px', fontWeight:'700' }}>
                                      Éch.{f.numero_echeance}
                                    </span>
                                  )}
                                </div>
                                {isAdmin && f.Client && (
                                  <div style={{ fontSize:'12px', color:theme.textSecondary, marginTop:'2px' }}>
                                    {formatNomClient(f.Client)}
                                    {isPro && <span style={{ marginLeft:'5px', fontSize:'10px', background:'#1565c0',
                                      color:'white', padding:'1px 4px', borderRadius:'3px' }}>PRO</span>}
                                  </div>
                                )}
                                <div style={{ fontSize:'11px', color:theme.textMuted, marginTop:'1px' }}>
                                  {new Date(f.date_emission).toLocaleDateString('fr-BE')}
                                </div>
                              </div>
                              <div style={{ textAlign:'right', flexShrink:0 }}>
                                <div style={{ fontWeight:'800', fontSize:'15px', color:theme.primary }}>
                                  {parseFloat(f.montant_ttc||0).toFixed(2)} €
                                </div>
                                <div style={{ marginTop:'3px' }}>
                                  <StatutBadge statut={f.statut_paiement} />
                                </div>
                              </div>
                            </div>
                            {/* Ligne 2 : Boutons */}
                            <div style={{ display:'flex', gap:'5px', flexWrap:'wrap', marginTop:'4px' }}>
                              <button onClick={e => { e.stopPropagation(); downloadPDF(f.id_facture, f.numero_facture); }}
                                style={{ ...S.btn, background:theme.successBg, color:theme.success,
                                  fontSize:'11px', padding:'3px 8px' }}>
                                📥 PDF
                              </button>
                              {isAdmin && (
                                <button onClick={e => { e.stopPropagation(); openPaiModal(f); }}
                                  style={{ ...S.btn, fontSize:'11px', padding:'3px 8px',
                                    background: f.statut_paiement==='payee' ? theme.successBg : f.statut_paiement==='en_retard' ? '#fef2f2' : '#fff8e1',
                                    color:      f.statut_paiement==='payee' ? theme.success   : f.statut_paiement==='en_retard' ? '#dc2626'   : '#f57f17' }}>
                                  {f.statut_paiement==='payee' ? '✅ Payée' : f.statut_paiement==='en_retard' ? '⚠️ Retard' : '⏳ Non payée'}
                                </button>
                              )}
                              {!isAdmin && !['payee','annulee'].includes(f.statut_paiement) && (
                                <button onClick={e => { e.stopPropagation(); openVirement(f); }}
                                  style={{ ...S.btn, background:'#e65100', color:'white',
                                    fontSize:'11px', padding:'3px 8px' }}>
                                  💳 Virement
                                </button>
                              )}
                              {isAdmin && isPro && (
                                <span style={{ fontSize:'10px', color: peppolStatus[f.id_facture]||f.envoyee_peppol
                                  ? theme.success : theme.textMuted, marginLeft:'auto',
                                  display:'flex', alignItems:'center' }}>
                                  {peppolStatus[f.id_facture]?.sent||f.envoyee_peppol ? '✅ Peppol' : ''}
                                </span>
                              )}
                              <span style={{ marginLeft:'auto', fontSize:'12px', color:theme.textMuted }}>
                                {isExpanded ? '▲' : '▼'}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : (
                      <tr
                        style={{ borderBottom: '1px solid #f0f0f0', background: rowBg, cursor: 'pointer', transition: 'background .15s' }}
                        onClick={() => setExpandedId(isExpanded ? null : f.id_facture)}
                      >
                        <td style={{ padding: '13px 14px' }}>
                          <span style={{ fontWeight: '700', color: theme.primary }}>{f.numero_facture}</span>
                          {f.numero_echeance && (
                            <span style={{ marginLeft: '6px', fontSize: '11px', background: theme.primaryBg, color: theme.primary, padding: '1px 7px', borderRadius: '10px' }}>
                              Éch. {f.numero_echeance}/{f.Devi?.config_echeancier?.length || (f.Devi?.has_echeancier ? 3 : 1)}
                            </span>
                          )}
                        </td>

                        {isAdmin && (
                          <td style={{ padding: '13px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontSize: '14px' }}>
                                {formatNomClient(f.Client)}
                              </span>
                              {isPro && <ProBadge />}
                            </div>
                            {isPro && (
                              <div style={{ fontSize: '11px', color: '#1565c0', marginTop: '2px' }}>
                                🏢 {f.Client.numero_tva}
                              </div>
                            )}
                          </td>
                        )}

                        <td style={{ padding: '13px 14px', fontSize: '13px', color: theme.textSecondary }}>
                          {new Date(f.date_emission).toLocaleDateString('fr-BE')}
                        </td>

                        {!isMobile && (
                          <td style={{ padding: '13px 14px', fontSize: '13px',
                            color: echeanceDepassee ? theme.danger : theme.textSecondary,
                            fontWeight: echeanceDepassee ? '700' : '400' }}>
                            {f.date_echeance
                              ? new Date(f.date_echeance).toLocaleDateString('fr-BE')
                              : '—'}
                            {echeanceDepassee && ' ⚠️'}
                          </td>
                        )}

                        <td style={{ padding: '13px 14px', textAlign: 'right', fontWeight: '700', fontSize: '15px', color: theme.textPrimary }}>
                          {parseFloat(f.montant_ttc || 0).toFixed(2)} €
                        </td>

                        <td style={{ padding: '13px 14px', textAlign: 'center' }}>
                          <StatutBadge statut={f.statut_paiement} />
                        </td>

                        {/* Bouton PDF */}
                        <td style={{ padding: '13px 14px', textAlign: 'center' }}>
                          <button
                            onClick={e => { e.stopPropagation(); downloadPDF(f.id_facture, f.numero_facture); }}
                            style={{ ...S.btn, background: theme.successBg, color: theme.success }}
                          >
                            📥 PDF
                          </button>
                          {isAdmin && (
                            <button
                              onClick={e => { e.stopPropagation(); openPaiModal(f); }}
                              title="Modifier le statut de paiement"
                              style={{ ...S.btn,
                                background: f.statut_paiement === 'payee' ? '#e8f5e9' : f.statut_paiement === 'en_retard' ? '#fff3f3' : '#fff8e1',
                                color:      f.statut_paiement === 'payee' ? '#2e7d32' : f.statut_paiement === 'en_retard' ? '#c62828' : '#f57f17'
                              }}
                            >
                              {f.statut_paiement === 'payee' ? '✅ Payée' : f.statut_paiement === 'en_retard' ? '⚠ Retard' : '⏳ Non payée'}
                            </button>
                          )}
                          {/* F4 — Bouton virement (client seulement, facture non payée) */}
                          {!isAdmin && !['payee', 'annulee'].includes(f.statut_paiement) && (
                            <button
                              onClick={e => { e.stopPropagation(); openVirement(f); }}
                              title="Payer par virement bancaire"
                              style={{ ...S.btn, background: '#e65100', color: 'white', marginTop: '4px', width: '100%', justifyContent: 'center' }}
                            >
                              💳 Payer par virement
                            </button>
                          )}
                        </td>

                        {/* Colonne Peppol — admin uniquement */}
                        {isAdmin && (
                          <td style={{ padding: '13px 14px', textAlign: 'center' }}>
                            {isPro ? (
                              <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>

                                {/* Statut envoi */}
                                <PeppolBadge sent={isSent} loading={isStsLoading} />

                                {/* Bouton XML — téléchargement UBL */}
                                <button
                                  onClick={e => { e.stopPropagation(); downloadXML(f.id_facture, f.numero_facture); }}
                                  title="Télécharger le fichier XML UBL 2.1"
                                  style={{ ...S.btn, background: '#1a237e', color: 'white' }}
                                >
                                  📄 XML
                                </button>

                                {/* Bouton prévisualisation */}
                                <button
                                  onClick={e => { e.stopPropagation(); previewXML(f.id_facture); }}
                                  title="Prévisualiser le XML avant envoi"
                                  style={{ ...S.btn, background: theme.infoBg, color: theme.info }}
                                >
                                  🔍
                                </button>

                                {/* Bouton envoi Peppol */}
                                {!isSent ? (
                                  <button
                                    onClick={e => { e.stopPropagation(); sendPeppol(f); }}
                                    disabled={isLoading}
                                    title="Envoyer via le réseau Peppol"
                                    style={{
                                      ...S.btn,
                                      background: isLoading ? '#e0e0e0' : theme.warningBg,
                                      color:      isLoading ? '#888'     : theme.warning,
                                      cursor:     isLoading ? 'wait'     : 'pointer',
                                    }}
                                  >
                                    {isLoading ? '⏳ Envoi…' : '📤 Peppol'}
                                  </button>
                                ) : (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                    <span style={{ fontSize: '11px', color: theme.success, fontWeight: '700' }}>
                                      ✅ {f.date_envoi_peppol
                                        ? new Date(f.date_envoi_peppol).toLocaleDateString('fr-BE')
                                        : 'Envoyé'}
                                    </span>
                                    {/* Bouton reset Peppol — visible si admin (rejet ou erreur) */}
                                    <button
                                      onClick={e => { e.stopPropagation(); resetPeppol(f); }}
                                      title="Réinitialiser statut Peppol (si rejet ou erreur d'envoi)"
                                      style={{ ...S.btn, background: '#fff7ed', color: '#c2410c',
                                        border: '1px solid #fed7aa', fontSize: '10px', padding: '2px 6px',
                                        lineHeight: '1.4' }}
                                    >
                                      🔄 Renvoyer
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              /* Client particulier — pas de Peppol */
                              <span style={{ fontSize: '11px', color: '#bbb' }} title="Client sans numéro de TVA — Peppol non applicable">
                                — Particulier
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                      )}

                
            {/* ── MODAL FACTURE RECTIFICATIVE / INDÉPENDANTE — 2 ÉTAPES ─────────── */}
      {showRectifModal && (
        <div style={S.overlay} onClick={() => { setShowRectifModal(false); setRectifStep(1); }}>
          <div style={{ ...S.modal, maxWidth: rectifStep === 2 ? '820px' : '600px', width: '97%',
            maxHeight: '92vh', overflowY: 'auto', transition: 'max-width 0.2s' }}
            onClick={e => e.stopPropagation()}>

            {/* ── En-tête avec stepper ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <h2 style={{ color: '#7c3aed', margin: 0, fontSize: '18px' }}>➕ Facture rectificative</h2>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                {[1,2].map(s => (
                  <span key={s} style={{ width: '28px', height: '28px', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: '700',
                    background: rectifStep === s ? '#7c3aed' : (rectifStep > s ? '#22c55e' : '#e5e7eb'),
                    color: rectifStep >= s ? 'white' : '#6b7280' }}>
                    {rectifStep > s ? '✓' : s}
                  </span>
                ))}
              </div>
            </div>
            <p style={{ color: theme.textSecondary, fontSize: '11px', marginBottom: '16px' }}>
              {rectifStep === 1 ? 'Étape 1 — Sélectionner le client et la facture à corriger'
                                : 'Étape 2 — Modifier les postes, quantités et prix'}
            </p>

            {/* ══════════════════════════════════════════════════════════════════
                ÉTAPE 1 — Sélection + références
            ══════════════════════════════════════════════════════════════════ */}
            {rectifStep === 1 && (<>

              {/* Client */}
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600',
                  color: theme.textSecondary, textTransform: 'uppercase', marginBottom: '4px' }}>Client *</label>
                <select value={rectifForm.id_client}
                  onChange={async e => {
                    const cid = e.target.value;
                    setRectifForm(p => ({ ...p, id_client: cid, facture_rectifiee: '', taux_tva: '21', note_credit_ref: '', id_devis: '', description: '' }));
                    setClientFactures([]); setRectifLignes([]);
                    if (cid) {
                      try {
                        const tk = localStorage.getItem('token');
                        const res = await axios.get(`${API_URL}/factures`, { headers: { Authorization: `Bearer ${tk}` } });
                        const all = res.data?.data || res.data || [];
                        setClientFactures(all.filter(f => f.id_client === parseInt(cid) || f.Client?.id_client === parseInt(cid)));
                      } catch(_) {}
                    }
                  }}
                  style={S.input}>
                  <option value="">— Sélectionner un client —</option>
                  {clients.map(c => (
                    <option key={c.id_client} value={c.id_client}>
                      {c.nom} {c.prenom || ''}{c.numero_tva ? ' · ' + c.numero_tva : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Facture à rectifier — s'affiche quand client sélectionné */}
              {clientFactures.length > 0 && (
                <div style={{ marginBottom: '10px', padding: '10px 12px',
                  background: '#fff8f0', border: '1px solid #fed7aa', borderRadius: '8px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700',
                    color: '#92400e', textTransform: 'uppercase', marginBottom: '6px' }}>
                    📋 Facture à annuler/corriger
                  </label>
                  <select value={rectifForm.facture_rectifiee}
                    onChange={async e => {
                      const fnum = e.target.value;
                      const sel  = clientFactures.find(f => f.numero_facture === fnum);
                      if (sel) {
                        const ht   = parseFloat(sel.montant_ht  || 0);
                        const tva  = parseFloat(sel.montant_tva || 0);
                        const taux = ht > 0 ? Math.round(tva / ht * 100) : 21;
                        let ncRef = '';
                        try {
                          const tk  = localStorage.getItem('token');
                          const ncRes = await axios.get(`${API_URL}/notes-credit`, { headers: { Authorization: `Bearer ${tk}` } });
                          const ncs   = ncRes.data?.data || ncRes.data || [];
                          const nc    = ncs.find(n => n.id_facture === sel.id_facture);
                          if (nc) ncRef = nc.numero_note_credit || '';
                        } catch(_) {}
                        setRectifForm(p => ({
                          ...p,
                          facture_rectifiee: fnum,
                                                    taux_tva:          String(taux),
                          note_credit_ref:   ncRef,
                          id_devis:          sel.id_devis ? String(sel.id_devis) : p.id_devis,
                          description:       p.description || ('Remplacement ' + fnum),
                        }));
                        // Charger les lignes du devis pour l étape 2
                        if (sel.id_devis) {
                          try {
                            const tk2   = localStorage.getItem('token');
                            const devRes = await axios.get(`${API_URL}/devis`, { headers: { Authorization: `Bearer ${tk2}` } });
                            const devs   = devRes.data?.data || devRes.data || [];
                            const dev    = devs.find(d => d.id_devis === sel.id_devis);
                            const taux2  = taux; // taux de la facture sélectionnée
                            if (dev?.LigneDevis?.length > 0) {
                              setRectifLignes(dev.LigneDevis
                                .filter(l => (l.type_ligne||'').toLowerCase() !== 'section' && (l.type_ligne||'').toLowerCase() !== 'sous_total')
                                .sort((a,b) => (a.ordre||0)-(b.ordre||0))
                                .map(l => ({
                                  _id:          l.id_ligne || (Math.random() + ''),
                                  description:  l.description_personnalisee || l.Prestation?.nom || '',
                                  quantite:     parseFloat(l.quantite || 1),
                                  prix_unitaire:parseFloat(l.prix_unitaire || 0),
                                   tva:          parseFloat(l.tva ?? taux2),
                                })));
                            }
                          } catch(_) {}
                        }
                      } else {
                        setRectifForm(p => ({ ...p, facture_rectifiee: fnum }));
                        setRectifLignes([]);
                      }
                    }}
                    style={S.input}>
                    <option value="">— Sélectionner la facture à rectifier —</option>
                    {clientFactures.map(f => (
                      <option key={f.id_facture} value={f.numero_facture}>
                        {f.numero_facture} · {parseFloat(f.montant_ttc||0).toFixed(2)} € · {f.statut_paiement}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Champs texte : description, motif, NC ref, TVA */}
              {/* ── Champs de l étape 1 : description, motif, NC, TVA ── */}
              {/* montant_ht retiré : calculé automatiquement depuis les postes de l étape 2 */}
              {[
                { key: 'description',      label: 'Description / Objet *',   type: 'text', placeholder: '' },
                { key: 'motif_correction', label: 'Motif de correction',      type: 'text', placeholder: 'ex: Correction TVA 0%→21%, ajustement prix...' },
                { key: 'note_credit_ref',  label: 'Note de crédit (réf)',     type: 'text', placeholder: 'ex: NC-2026-0001' },
                { key: 'taux_tva',         label: 'Taux TVA par défaut (nouvelles lignes)', type: 'tva' },
              ].map(field => (
                <div key={field.key} style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600',
                    color: theme.textSecondary, textTransform: 'uppercase', marginBottom: '4px' }}>
                    {field.label}
                  </label>
                  {field.type === 'tva' ? (
                    <select value={rectifForm.taux_tva}
                      onChange={e => {
                        // Garde uniquement comme taux par défaut pour les nouvelles lignes
                        // NE PAS propager aux lignes existantes — chaque ligne garde son taux propre
                        setRectifForm(p => ({ ...p, taux_tva: e.target.value }));
                      }}
                      style={S.input}>
                      <option value="0">0% — Autoliquidation / Exonération</option>
                      <option value="6">6% — Taux réduit</option>
                      <option value="12">12%</option>
                      <option value="21">21% — Taux normal</option>
                    </select>
                  ) : (
                    <input type="text" value={rectifForm[field.key]} placeholder={field.placeholder}
                      onChange={e => setRectifForm(p => ({ ...p, [field.key]: e.target.value }))}
                      style={S.input} />
                  )}
                </div>
              ))}

              <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                <button
                  onClick={() => {
                    if (!rectifForm.id_client) return alert('Sélectionnez un client.');
                    if (!rectifForm.description?.trim()) return alert('Saisissez une description.');
                    // Si pas de lignes chargées, ajouter une ligne vide
                    if (rectifLignes.length === 0) {
                      setRectifLignes([{ _id: '1', description: rectifForm.description || '', quantite: 1, prix_unitaire: 0, tva: parseFloat(rectifForm.taux_tva || 21) }]);
                    }
                    setRectifStep(2);
                  }}
                  style={{ ...S.btnPrimary, background: '#7c3aed' }}>
                  Suivant → Modifier les postes
                </button>
                <button onClick={() => { setShowRectifModal(false); setRectifStep(1); }} style={S.btnGrey}>
                  Annuler
                </button>
              </div>
            </>)}

            {/* ══════════════════════════════════════════════════════════════════
                ÉTAPE 2 — Tableau des postes éditables
            ══════════════════════════════════════════════════════════════════ */}
            {rectifStep === 2 && (<>
              <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '10px' }}>
                <strong>Client :</strong> {formatNomClient(clients.find(c => c.id_client === parseInt(rectifForm.id_client)))} &nbsp;|&nbsp;
                <strong>Facture :</strong> {rectifForm.facture_rectifiee || '—'} &nbsp;|&nbsp;
                <strong>NC :</strong> {rectifForm.note_credit_ref || '—'} &nbsp;|&nbsp;
                <strong>TVA :</strong> {rectifForm.taux_tva}%
              </div>

              {/* Tableau postes */}
              <div style={{ overflowX: 'auto', marginBottom: '12px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#f3f4f6' }}>
                      <th style={{ padding: '8px', textAlign: 'left', width: '38%' }}>Description</th>
                      <th style={{ padding: '8px', textAlign: 'right', width: '12%' }}>Qté</th>
                      <th style={{ padding: '8px', textAlign: 'right', width: '16%' }}>P.U. HT (€)</th>
                      <th style={{ padding: '8px', textAlign: 'center', width: '10%' }}>TVA%</th>
                      <th style={{ padding: '8px', textAlign: 'right', width: '14%' }}>Total HT</th>
                      <th style={{ padding: '8px', textAlign: 'center', width: '10%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rectifLignes.map((l, idx) => (
                      <tr key={l._id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '4px' }}>
                          <input value={l.description}
                            onChange={e => setRectifLignes(prev => prev.map((r,i) => i===idx ? { ...r, description: e.target.value } : r))}
                            style={{ ...S.input, fontSize: '12px', padding: '4px 6px', marginBottom: 0 }} />
                        </td>
                        <td style={{ padding: '4px' }}>
                          <input type="number" step="0.01" value={l.quantite}
                            onChange={e => setRectifLignes(prev => prev.map((r,i) => i===idx ? { ...r, quantite: parseFloat(e.target.value)||0 } : r))}
                            style={{ ...S.input, fontSize: '12px', padding: '4px 6px', marginBottom: 0, textAlign: 'right', width: '70px' }} />
                        </td>
                        <td style={{ padding: '4px' }}>
                          <input type="number" step="0.01" value={l.prix_unitaire}
                            onChange={e => setRectifLignes(prev => prev.map((r,i) => i===idx ? { ...r, prix_unitaire: parseFloat(e.target.value)||0 } : r))}
                            style={{ ...S.input, fontSize: '12px', padding: '4px 6px', marginBottom: 0, textAlign: 'right', width: '90px' }} />
                        </td>
                        <td style={{ padding: '4px', textAlign: 'center' }}>
                          <input type="number" step="1" value={l.tva}
                            onChange={e => setRectifLignes(prev => prev.map((r,i) => i===idx ? { ...r, tva: parseFloat(e.target.value)||0 } : r))}
                            style={{ ...S.input, fontSize: '12px', padding: '4px 6px', marginBottom: 0, textAlign: 'center', width: '55px' }} />
                        </td>
                        <td style={{ padding: '4px', textAlign: 'right', fontWeight: '600' }}>
                          {(l.quantite * l.prix_unitaire).toFixed(2)}
                        </td>
                        <td style={{ padding: '4px', textAlign: 'center' }}>
                          <button onClick={() => setRectifLignes(prev => prev.filter((_,i) => i!==idx))}
                            style={{ background: '#fee2e2', border: 'none', borderRadius: '4px',
                              color: '#dc2626', padding: '2px 6px', cursor: 'pointer', fontSize: '13px' }}>
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Ajouter une ligne */}
              <button onClick={() => setRectifLignes(prev => [...prev, { _id: Date.now()+'', description: '', quantite: 1, prix_unitaire: 0, tva: parseFloat(rectifForm.taux_tva||21) }])}
                style={{ ...S.btn, marginBottom: '12px', fontSize: '12px',
                  background: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac' }}>
                + Ajouter un poste
              </button>

              {/* Récapitulatif */}
              {(() => {
                const htTotal  = rectifLignes.reduce((s,l) => s + l.quantite * l.prix_unitaire, 0);
                const tvaTotal = rectifLignes.reduce((s,l) => s + l.quantite * l.prix_unitaire * l.tva/100, 0);
                return (
                  <div style={{ padding: '10px 14px', background: '#f5f3ff', borderRadius: '8px',
                    marginBottom: '14px', fontSize: '13px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    <span>HT : <strong>{htTotal.toFixed(2)} €</strong></span>
                    <span>TVA : <strong>{tvaTotal.toFixed(2)} €</strong></span>
                    <span style={{ color: '#7c3aed', fontWeight: '700', fontSize: '14px' }}>
                      TTC : <strong>{(htTotal + tvaTotal).toFixed(2)} €</strong>
                    </span>
                  </div>
                );
              })()}

              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setRectifStep(1)} style={S.btnGrey}>
                  ← Retour
                </button>
                <button onClick={creerFactureRectificative} disabled={rectifLoading}
                  style={{ ...S.btnPrimary, background: '#7c3aed', opacity: rectifLoading ? 0.6 : 1 }}>
                  {rectifLoading ? '…' : '✅ Créer la facture'}
                </button>
                <button onClick={() => { setShowRectifModal(false); setRectifStep(1); }} style={S.btnGrey}>
                  Annuler
                </button>
              </div>
            </>)}

          </div>
        </div>
      )}

      {/* ── Détail expandé ── */}
                      {isExpanded && (
                        <tr style={{ background: '#f8fafc' }}>
                          <td colSpan={isAdmin ? 8 : 5} style={{ padding: isMobile ? '0 6px 10px' : '0 14px 16px' }}>
                            <div style={{ background: 'white', borderRadius: '10px', border: `1px solid ${theme.border}`, overflow: 'hidden', marginTop: '8px' }}>

                              {/* Infos client (admin) */}
                              {isAdmin && f.Client && (
                                <div style={{ padding: '12px 16px', borderBottom: `1px solid ${theme.border}`, fontSize: '13px', display: 'flex', gap: '24px', flexWrap: 'wrap', background: '#fafbfc' }}>
                                  <div><span style={{ color: theme.textMuted }}>Client : </span><strong>{formatNomClient(f.Client)}</strong> {isPro && <ProBadge />}</div>
                                  <div><span style={{ color: theme.textMuted }}>Email : </span>
                                    {isRealEmail(f.Client.email)
                                      ? f.Client.email
                                      : <span style={{ fontSize:'11px', fontWeight:'600', padding:'2px 8px',
                                          borderRadius:'10px', background:'#fff3e0', color:'#e65100',
                                          border:'1px solid #ffcc80' }}>Sans email</span>
                                    }
                                  </div>
                                  {f.Client.telephone && <div><span style={{ color: theme.textMuted }}>Tél : </span>{f.Client.telephone}</div>}
                                  {f.Client.numero_tva && <div><span style={{ color: theme.textMuted }}>TVA : </span><strong style={{ color: '#1565c0' }}>{f.Client.numero_tva}</strong></div>}
                                  {f.Devi?.numero_devis && <div><span style={{ color: theme.textMuted }}>Réf. devis : </span>{f.Devi.numero_devis}</div>}
                                   {/* Bloc rectification — affiché si champs DB présents */}
                                   {(f.facture_rectifiee || f.note_credit_ref || f.motif_correction) && (
                                     <div style={{ marginTop: '8px', padding: '6px 10px',
                                       background: '#fffbeb', border: '1px solid #f59e0b',
                                       borderRadius: '6px', fontSize: '11px' }}>
                                       <div style={{ fontWeight: '700', color: '#92400e', marginBottom: '3px' }}>🔄 Facture rectificative</div>
                                       {f.facture_rectifiee && <div style={{ color: '#78350f' }}>Annule : <strong>{f.facture_rectifiee}</strong></div>}
                                       {f.note_credit_ref   && <div style={{ color: '#78350f' }}>Note de crédit : <strong>{f.note_credit_ref}</strong></div>}
                                       {f.motif_correction  && <div style={{ color: '#78350f' }}>Motif : {f.motif_correction}</div>}
                                     </div>
                                   )}
                                </div>
                              )}

                              {/* Lignes de la facture */}
                              {isMobile ? (
                                /* Mobile : cards par ligne — même logique que pdf_helpers */
                                <div style={{ display:'flex', flexDirection:'column', gap:'7px', padding:'8px 0' }}>
                                  {([...(f.Devi?.LigneDevis || f.LigneFactures || [])].sort((a,b)=>(a.ordre||0)-(b.ordre||0))).length > 0
                                    ? ([...(f.Devi?.LigneDevis || f.LigneFactures || [])].sort((a,b)=>(a.ordre||0)-(b.ordre||0))).map(function(l, i) {
                                        var typeLg   = (l.type_ligne || 'article').toLowerCase();
                                        var montantL = parseFloat(l.prix_unitaire||0) * parseFloat(l.quantite||1);
                                        if (typeLg === 'section') return (
                                          <div key={i} style={{ background:'#c8dff0', borderRadius:'8px',
                                            padding:'8px 12px', borderLeft:'4px solid #1e3a5f' }}>
                                            <div style={{ fontSize:'10px', color:'#5a7a9a', fontWeight:'700',
                                              textTransform:'uppercase' }}>Section {l.numero_section||''}</div>
                                            <div style={{ fontSize:'13px', fontWeight:'900', color:'#1e3a5f' }}>
                                              {l.description_personnalisee||'—'}
                                            </div>
                                          </div>
                                        );
                                        if (typeLg === 'sous_total') return (
                                          <div key={i} style={{ background:'#ede9fe', borderRadius:'8px',
                                            padding:'8px 12px', display:'flex', justifyContent:'space-between',
                                            alignItems:'center', borderLeft:'3px solid #7c3aed' }}>
                                            <span style={{ fontSize:'12px', fontWeight:'700', color:'#7c3aed' }}>
                                              {l.description_personnalisee||'Sous-total'}
                                            </span>
                                            <span style={{ fontSize:'13px', fontWeight:'900', color:'#7c3aed' }}>
                                              {montantL.toFixed(2)} €
                                            </span>
                                          </div>
                                        );
                                        return (
                                          <div key={i} style={{ background:'white', borderRadius:'10px',
                                            padding:'10px 12px', border:'1px solid #e5eaf0',
                                            boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
                                            <div style={{ display:'flex', gap:'5px', marginBottom:'7px',
                                              alignItems:'flex-start' }}>
                                              <span style={{ fontSize:'10px', fontWeight:'700', color:'#9ca3af',
                                                background:'#f1f5f9', borderRadius:'4px', padding:'2px 6px',
                                                flexShrink:0 }}>{l.numero_section||i+1}</span>
                                              <div style={{ fontSize:'13px', fontWeight:'600', color:'#1e293b',
                                                lineHeight:1.4, flex:1 }}>{l.description_personnalisee||'—'}</div>
                                            </div>
                                            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)',
                                              gap:'5px' }}>
                                              <div style={{ background:'#f8fafc', borderRadius:'6px',
                                                padding:'5px 8px', textAlign:'center' }}>
                                                <div style={{ color:'#94a3b8', fontSize:'9px', fontWeight:'700',
                                                  textTransform:'uppercase', marginBottom:'2px' }}>Qté</div>
                                                <div style={{ fontWeight:'700', fontSize:'12px', color:'#1e293b' }}>
                                                  {l.quantite||1}{l.unite?' '+l.unite:''}
                                                </div>
                                              </div>
                                              <div style={{ background:'#f8fafc', borderRadius:'6px',
                                                padding:'5px 8px', textAlign:'center' }}>
                                                <div style={{ color:'#94a3b8', fontSize:'9px', fontWeight:'700',
                                                  textTransform:'uppercase', marginBottom:'2px' }}>P.U. HT</div>
                                                <div style={{ fontWeight:'700', fontSize:'12px', color:'#1e293b' }}>
                                                  {parseFloat(l.prix_unitaire||0).toFixed(2)} €
                                                </div>
                                              </div>
                                              <div style={{ background:'#EFF6FF', borderRadius:'6px',
                                                padding:'5px 8px', textAlign:'center',
                                                border:'1px solid #bfdbfe' }}>
                                                <div style={{ color:'#1d4ed8', fontSize:'9px', fontWeight:'700',
                                                  textTransform:'uppercase', marginBottom:'2px' }}>Total HT</div>
                                                <div style={{ fontWeight:'800', fontSize:'12px', color:'#1d4ed8' }}>
                                                  {montantL.toFixed(2)} €
                                                </div>
                                              </div>
                                            </div>
                                            <div style={{ marginTop:'4px', fontSize:'10px', color:'#94a3b8' }}>
                                              TVA {l.tva||0}%
                                            </div>
                                          </div>
                                        );
                                      })
                                    : <div style={{ padding:'12px', textAlign:'center', color:theme.textMuted,
                                        fontSize:'12px' }}>
                                        {f.notes ? f.notes.replace(/_peppol.*$/, '').replace(/\{.*$/, '').trim()
                                          : 'Aucune ligne'}
                                      </div>
                                  }
                                </div>
                              ) : (
                                /* Desktop: table originale INCHANGÉE */
                                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '400px' }}>
                                  <thead>
                                    <tr style={{ background: theme.primaryBg }}>
                                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '12px', color: theme.textSecondary }}>#</th>
                                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '12px', color: theme.textSecondary }}>Description</th>
                                      <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '12px', color: theme.textSecondary }}>P.U. HT</th>
                                      <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: '12px', color: theme.textSecondary }}>TVA%</th>
                                      <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '12px', color: theme.textSecondary }}>Total HT</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(() => {
                                      const _lignesD = [...(f.Devi?.LigneDevis || f.LigneFactures || [])]
                                        .sort((a,b) => (a.ordre||0) - (b.ordre||0));
                                      if (!_lignesD.length) return [
                                        <tr key="_empty"><td colSpan={5} style={{ padding:'14px',
                                          textAlign:'center', color:theme.textMuted, fontSize:'12px' }}>
                                          {f.notes ? f.notes.replace(/_peppol.*$/, '').replace(/\{.*$/, '').trim() : 'Aucune ligne'}
                                        </td></tr>
                                      ];
                                      return _lignesD.map((l, i) => {
                                        const tl  = (l.type_ligne || 'article').toLowerCase();
                                        const tot = parseFloat(l.prix_unitaire||0) * parseFloat(l.quantite||1);
                                        if (tl === 'section') return (
                                          <tr key={l.id_ligne_devis||i} style={{ background:'#c8dff0' }}>
                                            <td colSpan={5} style={{ padding:'8px 12px', fontWeight:'900',
                                              fontSize:'12px', color:'#1e3a5f' }}>
                                              {l.numero_section ? <span style={{ marginRight:'8px', opacity:0.6 }}>{l.numero_section}</span> : null}
                                              {l.description_personnalisee||'—'}
                                            </td>
                                          </tr>
                                        );
                                        if (tl === 'sous_total') return (
                                          <tr key={l.id_ligne_devis||i} style={{ background:'#ede9fe', borderTop:'1px solid #ddd6fe' }}>
                                            <td colSpan={4} style={{ padding:'7px 12px', fontStyle:'italic',
                                              fontSize:'12px', color:'#7c3aed', fontWeight:'600' }}>
                                              {l.description_personnalisee||'Sous-total'}
                                            </td>
                                            <td style={{ padding:'7px 12px', textAlign:'right', fontWeight:'800',
                                              fontSize:'13px', color:'#7c3aed' }}>
                                              {tot.toFixed(2)} €
                                            </td>
                                          </tr>
                                        );
                                        return (
                                          <tr key={l.id_ligne_devis||i} style={{ borderBottom:`1px solid ${theme.border}` }}>
                                            <td style={{ padding:'9px 12px', fontSize:'13px', color:theme.textMuted }}>{l.numero_section||i+1}</td>
                                            <td style={{ padding:'9px 12px', fontSize:'13px' }}>{l.description_personnalisee}</td>
                                            <td style={{ padding:'9px 12px', textAlign:'right', fontSize:'13px' }}>{parseFloat(l.prix_unitaire||0).toFixed(2)} €</td>
                                            <td style={{ padding:'9px 12px', textAlign:'center', fontSize:'13px' }}>{l.tva}%</td>
                                            <td style={{ padding:'9px 12px', textAlign:'right', fontSize:'13px', fontWeight:'600' }}>
                                              {tot.toFixed(2)} €
                                            </td>
                                          </tr>
                                        );
                                      });
                                    })()}
                                  </tbody>
                                </table>
                                </div>
                              )}

                              {/* Totaux */}
                              <div style={{ padding: isMobile ? '8px 12px' : '12px 16px', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: isMobile ? 'space-between' : 'flex-end', flexWrap:'wrap', gap: isMobile ? '8px' : '24px', fontSize: '13px' }}>
                                <span style={{ color: theme.textMuted }}>HT : <strong>{parseFloat(f.montant_ht || 0).toFixed(2)} €</strong></span>
                                <span style={{ color: theme.textMuted }}>TVA : <strong>{parseFloat(f.montant_tva || 0).toFixed(2)} €</strong></span>
                                <span style={{ color: theme.primary, fontWeight: '700', fontSize: '15px' }}>TTC : {parseFloat(f.montant_ttc || 0).toFixed(2)} €</span>
                              </div>

                               {/* Actions admin */}
                               {isAdmin && (
                                 <div style={{ padding: '10px 16px', borderTop: '1px solid #e5e7eb',
                                   display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                   <button onClick={() => downloadPDF(f.id_facture, f.numero_facture)}
                                     style={{ ...S.btn, background: '#e0f2fe', color: '#0369a1',
                                       fontSize: '12px', border: 'none', cursor: 'pointer', padding: '5px 10px' }}>
                                     PDF
                                   </button>
                                   {!f.envoyee_peppol && (
                                     <button onClick={() => deleteFacture(f)}
                                       style={{ ...S.btn, background: '#fef2f2', color: '#dc2626',
                                         border: '1px solid #fca5a5', fontSize: '12px', padding: '5px 10px' }}>
                                       Supprimer
                                     </button>
                                   )}
                                   {f.envoyee_peppol && (
                                     <div style={{ fontSize: '11px', color: '#059669' }}>
                                       <div>✅ Envoyée Peppol</div>
                                       {peppolStatus[f.id_facture]?.peppol_message_id && (
                                         <div style={{ color: '#6b7280', marginTop: 2 }}>
                                           ID : <code style={{ background: '#f0fdf4', padding: '1px 4px', borderRadius: 3 }}>
                                             {peppolStatus[f.id_facture].peppol_message_id}
                                           </code>
                                         </div>
                                       )}
                                       {peppolStatus[f.id_facture]?.peppol_status && (
                                         <div style={{ color: '#6b7280', marginTop: 2 }}>
                                           Statut : <strong>{peppolStatus[f.id_facture].peppol_status}</strong>
                                         </div>
                                       )}
                                       {peppolStatus[f.id_facture]?.date_envoi && (
                                         <div style={{ color: '#6b7280', marginTop: 2 }}>
                                           Envoyée le : {new Date(peppolStatus[f.id_facture].date_envoi).toLocaleDateString('fr-BE')}
                                         </div>
                                       )}
                                       <div style={{ color: '#9ca3af', marginTop: 4 }}>NC pour annuler</div>
                                     </div>
                                   )}
                                 </div>
                               )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            </div>{/* /overflowX */}
          </div>
        )}
            </>
          );
        })()}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL — Prévisualisation XML UBL
      ══════════════════════════════════════════════════════════════════════ */}
      {showXmlModal && xmlPreview && (
        <div style={S.overlay} onClick={() => setShowXmlModal(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: '#1a237e' }}>🔍 Prévisualisation XML UBL 2.1</h3>
              <button onClick={() => setShowXmlModal(false)}
                style={{ ...S.btn, background: theme.dangerBg, color: theme.danger, fontSize: '14px', padding: '6px 12px' }}>
                ✕ Fermer
              </button>
            </div>

            {/* Infos facture */}
            <div style={{ background: '#e3f2fd', borderRadius: '8px', padding: '12px 16px', marginBottom: '14px', fontSize: '13px' }}>
              <strong>Facture :</strong> {xmlPreview.numero_facture} &nbsp;|&nbsp;
              <strong>Client :</strong> {xmlPreview.client_nom} &nbsp;|&nbsp;
              <strong>N° TVA :</strong> <span style={{ color: '#1565c0', fontWeight: '700' }}>{xmlPreview.client_tva}</span> &nbsp;|&nbsp;
              <strong>Montant TTC :</strong> {parseFloat(xmlPreview.montant_ttc || 0).toFixed(2)} €
            </div>
            <div style={{ background: '#e8f5e9', borderRadius: '8px', padding: '10px 16px', marginBottom: '14px', fontSize: '13px' }}>
              <strong>Destinataire Peppol :</strong> <code style={{ background: '#f0f0f0', padding: '2px 6px', borderRadius: '4px' }}>{xmlPreview.recipient_peppol}</code>
            </div>

            {/* XML */}
            <div style={{ position: 'relative' }}>
              <pre style={{
                background: '#1a1a2e', color: '#a8d8a8', borderRadius: '8px',
                padding: '16px', fontSize: '11px', maxHeight: '380px',
                overflowY: 'auto', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              }}>
                {xmlPreview.xml}
              </pre>
              <button
                onClick={() => { navigator.clipboard.writeText(xmlPreview.xml); alert('XML copié !'); }}
                style={{ ...S.btn, position: 'absolute', top: '10px', right: '10px', background: 'rgba(255,255,255,0.15)', color: 'white' }}
              >
                📋 Copier
              </button>
            </div>

            <p style={{ marginTop: '12px', fontSize: '12px', color: theme.textMuted }}>
              Vérifiez ce XML avant l'envoi. Le format UBL 2.1 BIS Billing 3.0 est conforme à la norme européenne EN 16931.
            </p>
          </div>
        </div>
      )}

      
{/* ── Modal statut paiement ─────────────────────────────────── */}
      {showPaiModal && paiTarget && (
        <div style={S.overlay} onClick={e => e.target===e.currentTarget && setShowPaiModal(false)}>
          <div style={{ ...S.modal, maxWidth: '460px' }}>
            <h2 style={{ margin: '0 0 6px', fontSize: '18px', color: theme.textPrimary }}>
              💰 Statut de paiement
            </h2>
            <p style={{ margin: '0 0 20px', fontSize: '13px', color: theme.textSecondary }}>
              Facture <strong>{paiTarget.numero_facture}</strong> · 
              Total TTC : <strong>{parseFloat(paiTarget.montant_ttc || 0).toFixed(2)} €</strong>
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '12px',
                  color: theme.textSecondary, marginBottom: '6px', textTransform: 'uppercase' }}>
                  Nouveau statut
                </label>
                <select value={paiForm.statut_paiement}
                  onChange={e => setPaiForm(p => ({ ...p, statut_paiement: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', border: `1px solid ${theme.border}`,
                    borderRadius: '8px', fontSize: '14px', background: '#fafafa' }}>
                  <option value="emise">📄 Émise (non payée)</option>
                  <option value="non_payee">⏳ Non payée</option>
                  <option value="partielle">🔶 Paiement partiel</option>
                  <option value="payee">✅ Payée intégralement</option>
                  <option value="en_retard">⚠️ En retard</option>
                </select>
              </div>

              {(paiForm.statut_paiement === 'payee' || paiForm.statut_paiement === 'partielle') && (
                <div>
                  <label style={{ display: 'block', fontWeight: '600', fontSize: '12px',
                    color: theme.textSecondary, marginBottom: '6px', textTransform: 'uppercase' }}>
                    Montant reçu (€)
                  </label>
                  <input type="number" step="0.01" value={paiForm.montant_paye}
                    onChange={e => setPaiForm(p => ({ ...p, montant_paye: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', border: `1px solid ${theme.border}`,
                      borderRadius: '8px', fontSize: '14px', background: '#fafafa',
                      boxSizing: 'border-box' }} />
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '22px' }}>
              <button onClick={saveStatutPaiement} disabled={paiLoading}
                style={{ flex: 1, padding: '11px', background: theme.primary, color: 'white',
                  border: 'none', borderRadius: '8px', fontWeight: '600',
                  cursor: paiLoading ? 'not-allowed' : 'pointer', opacity: paiLoading ? 0.7 : 1 }}>
                {paiLoading ? '⏳ Enregistrement…' : '💾 Enregistrer'}
              </button>
              <button onClick={() => setShowPaiModal(false)}
                style={{ padding: '11px 18px', background: '#f0f0f0', color: theme.textSecondary,
                  border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL — Paiement par virement (F4 — client)
      ══════════════════════════════════════════════════════════════════════ */}
      {modalVirement && (
        <div style={S.overlay} onClick={e => e.target===e.currentTarget && setModalVirement(null)}>
          <div style={{ ...S.modal, maxWidth: '480px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '18px', color: theme.textPrimary }}>
                  💳 Payer par virement
                </h2>
                <p style={{ margin: '3px 0 0', fontSize: '13px', color: theme.textSecondary }}>
                  {modalVirement.numero_facture}
                </p>
              </div>
              <button onClick={() => setModalVirement(null)}
                style={{ ...S.btn, background: theme.dangerBg, color: theme.danger, padding: '6px 12px', fontSize: '14px' }}>
                ✕
              </button>
            </div>

            {virLoading && (
              <div style={{ textAlign: 'center', padding: '30px', color: theme.textMuted }}>
                ⏳ Récupération des informations de virement…
              </div>
            )}

            {virError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '14px', color: '#dc2626', fontSize: '13px' }}>
                ❌ {virError}
              </div>
            )}

            {virInfos && !virLoading && (
              <>
                {virSent && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#15803d' }}>
                    ✅ Informations de virement disponibles ci-dessous.
                  </div>
                )}
                {/* Coordonnées bancaires */}
                <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '16px 20px', marginBottom: '14px', border: `1px solid ${theme.border}` }}>
                  <p style={{ margin: '0 0 10px', fontSize: '11px', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>Coordonnées bancaires</p>
                  {[['Bénéficiaire', 'MKW Solutions', false], ['IBAN', virInfos.iban, true], ['BIC / SWIFT', virInfos.bic, true]].map(([lbl, val, mono]) => (
                    <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${theme.border}` }}>
                      <span style={{ fontSize: '13px', color: theme.textSecondary }}>{lbl}</span>
                      <span style={{ fontSize: '13px', fontWeight: '600', fontFamily: mono ? 'monospace' : 'inherit', letterSpacing: mono ? '1px' : 'normal' }}>{val}</span>
                    </div>
                  ))}
                </div>
                {/* Montant */}
                <div style={{ background: '#eff6ff', borderRadius: '10px', padding: '12px 20px', marginBottom: '14px', border: '1px solid #bfdbfe' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: theme.textSecondary }}>Montant à payer</span>
                    <span style={{ fontSize: '18px', fontWeight: '700', color: theme.primary }}>{virInfos.montant} €</span>
                  </div>
                </div>
                {/* OGM */}
                <div style={{ background: '#fff7ed', borderRadius: '10px', padding: '14px 20px', border: '2px solid #fed7aa' }}>
                  <p style={{ margin: '0 0 6px', fontSize: '11px', color: '#92400e', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '700' }}>
                    ⚠️ Communication obligatoire
                  </p>
                  <p style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#c2410c', fontFamily: 'monospace', letterSpacing: '3px', textAlign: 'center', padding: '8px 0' }}>
                    {virInfos.ogm}
                  </p>
                  <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#92400e', textAlign: 'center' }}>
                    Sans cette référence, votre paiement ne peut pas être identifié.
                  </p>
                </div>
                {/* QR SEPA — affiché si le backend le fournit */}
                {virInfos.qrBase64 && (
                  <div style={{ marginTop: '16px', background: '#f8fafc', borderRadius: '10px', padding: '16px', border: `1px solid ${theme.border}`, textAlign: 'center' }}>
                    <p style={{ margin: '0 0 10px', fontSize: '11px', color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>
                      📱 Payer par QR code SEPA
                    </p>
                    <img
                      src={virInfos.qrBase64}
                      alt="QR SEPA"
                      style={{ width: '160px', height: '160px', display: 'block', margin: '0 auto', imageRendering: 'pixelated' }}
                    />
                    <p style={{ margin: '8px 0 0', fontSize: '11px', color: theme.textMuted }}>
                      Scannez avec votre app bancaire (Belfius, ING, KBC, BNP…)
                    </p>
                  </div>
                )}
                <button onClick={() => setModalVirement(null)}
                  style={{ width: '100%', marginTop: '18px', padding: '12px', background: theme.primary, color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                  J'ai noté les informations — Fermer
                </button>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
};


export default Factures;
